import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEvents } from "@/hooks/useEvents";
import { PricingEvent } from "@/store/eventsSlice";

interface ReportErrorDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  company: PricingEvent;
  existingError?: {
    screenshotError: boolean;
    dataError: boolean;
    errorDesc: string;
  };
  onSubmitted?: () => void;  // Added this optional callback
}

const ReportErrorDialog = ({ 
  isOpen, 
  onOpenChange, 
  company,
  existingError,
  onSubmitted 
}: ReportErrorDialogProps) => {
  const [reportData, setReportData] = useState({
    badImage: false,
    badData: false,
    comments: ""
  });
  const { logError } = useEvents();

  useEffect(() => {
    if (isOpen && existingError) {
      setReportData({
        badImage: existingError.screenshotError,
        badData: existingError.dataError,
        comments: existingError.errorDesc
      });
    } else if (isOpen) {
      setReportData({
        badImage: false,
        badData: false,
        comments: ""
      });
    }
  }, [isOpen, existingError]);

  const handleReportSubmit = async () => {
    await logError(
      company.slug,
      company.v1,
      company.v2,
      reportData.badImage,
      reportData.badData,
      reportData.comments
    );
    onOpenChange(false);
    onSubmitted?.(); // Call the callback if provided
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report an Error</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="badImage" 
              checked={reportData.badImage}
              onCheckedChange={(checked) => 
                setReportData(prev => ({ ...prev, badImage: checked === true }))
              }
            />
            <Label htmlFor="badImage">Bad Image</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="badData" 
              checked={reportData.badData}
              onCheckedChange={(checked) => 
                setReportData(prev => ({ ...prev, badData: checked === true }))
              }
            />
            <Label htmlFor="badData">Bad Data</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="comments">Comments</Label>
            <Textarea
              id="comments"
              placeholder="Please provide additional details..."
              value={reportData.comments}
              onChange={(e) => 
                setReportData(prev => ({ ...prev, comments: e.target.value }))
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleReportSubmit}>
            Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportErrorDialog;